import c from 'classnames';
import { useEffect, useState } from 'react';

import { Icon, Typography } from '@repo/ui';
import { useAIMessageFeedback } from '../../../hooks/useAIMessageFeedback';
import { AIMessageFeedbackForm } from './AIMessageFeedbackForm';

import styles from './AIMessageFeedback.module.css';

const SUBMITTED_STAY_MS = 1500;
const FADE_OUT_MS = 250;

interface AIMessageFeedbackProps {
  messageId: string;
  isStreaming: boolean;
  onDismiss: () => void;
}

export const AIMessageFeedback = ({
  messageId,
  isStreaming,
  onDismiss,
}: AIMessageFeedbackProps) => {
  const {
    feedbackState,
    handleThumbsUp,
    handleThumbsDown,
    handleSubmitFeedback,
  } = useAIMessageFeedback(messageId, isStreaming);

  const [isEntering, setIsEntering] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setIsEntering(false));
    return () => cancelAnimationFrame(id);
  }, []);

  const isTerminal = feedbackState === 'like' || feedbackState === 'submitted';

  useEffect(() => {
    if (!isTerminal) return;

    const fadeTimer = setTimeout(() => setIsExiting(true), SUBMITTED_STAY_MS);
    const unmountTimer = setTimeout(
      () => onDismiss(),
      SUBMITTED_STAY_MS + FADE_OUT_MS,
    );

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(unmountTimer);
    };
  }, [isTerminal, onDismiss]);

  const showFeedbackForm =
    feedbackState === 'dislike' || feedbackState === 'submitted';

  return (
    <div
      className={c(styles.feedbackContainer, {
        [styles.entering]: isEntering,
        [styles.exiting]: isExiting,
      })}
    >
      <div className={styles.feedbackPrompt}>
        <Typography variant='caption' className={styles.promptText}>
          Was this helpful?
        </Typography>
        <div className={styles.thumbsButtons}>
          <button
            className={styles.thumbButton}
            onClick={handleThumbsUp}
            disabled={feedbackState !== 'none' || isStreaming}
            aria-label='Thumbs up'
          >
            <Icon
              name={feedbackState === 'like' ? 'thumbsUpFilled' : 'thumbsUp'}
              size='s'
              className={feedbackState === 'like' ? styles.iconSelected : ''}
            />
          </button>
          <button
            className={styles.thumbButton}
            onClick={handleThumbsDown}
            disabled={feedbackState !== 'none' || isStreaming}
            aria-label='Thumbs down'
          >
            <Icon
              name={
                feedbackState === 'dislike' || feedbackState === 'submitted'
                  ? 'thumbsDownFilled'
                  : 'thumbsDown'
              }
              size='s'
              className={
                feedbackState === 'dislike' || feedbackState === 'submitted'
                  ? styles.iconSelected
                  : ''
              }
            />
          </button>
        </div>
      </div>

      {showFeedbackForm && (
        <AIMessageFeedbackForm
          onSubmit={handleSubmitFeedback}
          isSubmitted={feedbackState === 'submitted'}
        />
      )}
    </div>
  );
};
